import React, { useMemo } from 'react';
import { SectionLabel } from '@/components/ui/section-label';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { Button } from '@/components/ui/button';
import { Boxes, Plus, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { useTranslation } from '@/hooks/useTranslation';
import { useMaterialLotManager } from '../hooks/useMaterialLotManager';
import { flattenIsoTree } from '../data/isoMaterialTree';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';

type MaterialLotManagerPanelProps = {
  traceabilityApiClient: TraceabilityApiClient | null;
};

export const MaterialLotManagerPanel: React.FC<MaterialLotManagerPanelProps> = ({
  traceabilityApiClient,
}) => {
  const { t } = useTranslation();
  const isoOptions = useMemo(() => flattenIsoTree(t), [t]);

  const {
    activeMaterials,
    lots,
    selectedMaterialId,
    formLot,
    isLoadingMaterials,
    isLoadingLots,
    isSaving,
    formError,
    successId,
    successDetails,
    setSelectedMaterialId,
    setFormLot,
    handleFindOrCreate,
    handleReset,
  } = useMaterialLotManager(traceabilityApiClient);

  const selectedMaterial = activeMaterials.find((m) => m.id === selectedMaterialId);

  const isoCategoryLabel = (path: string): string => {
    const found = isoOptions.find((o) => o.value === path);
    return found ? found.label : path;
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Section 1: Material picker — always visible */}
      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <SectionLabel icon={Boxes}>
            {t('traceability.materialLotManager.picker.sectionTitle')}
          </SectionLabel>
          {selectedMaterial && (
            <Badge tone="primary" caps>
              {selectedMaterial.name}
            </Badge>
          )}
        </div>

        <div>
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">
            {t('traceability.materialLotManager.picker.label')}
          </p>
          <SimpleSelect
            aria-label={t('traceability.materialLotManager.picker.label')}
            value={selectedMaterialId !== null ? String(selectedMaterialId) : null}
            onChange={(next) => setSelectedMaterialId(next ? Number(next) : null)}
            disabled={isLoadingMaterials || isSaving}
            noneLabel={t('traceability.materialLotManager.picker.placeholder')}
            options={activeMaterials.map((m) => ({ value: String(m.id), label: m.name }))}
          />
        </div>

        {selectedMaterial && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {selectedMaterial.isoCategory && (
              <Badge tone="primary" size="sm">
                {isoCategoryLabel(selectedMaterial.isoCategory)}
              </Badge>
            )}
            {selectedMaterial.designation && (
              <span className="font-mono text-2xs text-muted-foreground">
                {selectedMaterial.designation}
              </span>
            )}
          </div>
        )}
      </Card>

      {/* Section 2: Lot management — only when material selected */}
      {selectedMaterialId === null ? (
        <EmptyState
          icon={Boxes}
          title={t('traceability.materialLotManager.empty.title')}
          description={t('traceability.materialLotManager.empty.hint')}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          {/* Left: find-or-create form */}
          <Card>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <SectionLabel icon={Plus}>
                {t('traceability.materialLotManager.form.sectionTitle')}
              </SectionLabel>
              <span className="text-2xs text-muted-foreground">
                {t('traceability.materialLotManager.form.idempotent')}
              </span>
            </div>

            {formError && (
              <Callout tone="error" className="mb-4">
                {formError}
              </Callout>
            )}

            <SimpleInput
              name="lot-supplier"
              label={t('traceability.materialLotManager.form.fields.supplier.label')}
              value={formLot.supplier}
              onChange={(val) => setFormLot({ ...formLot, supplier: val as string })}
              placeholder={t('traceability.materialLotManager.form.fields.supplier.placeholder')}
            />

            <div className="mt-4">
              <SimpleInput
                name="lot-number"
                label={t('traceability.materialLotManager.form.fields.lotNumber.label')}
                value={formLot.lotNumber}
                onChange={(val) => setFormLot({ ...formLot, lotNumber: val as string })}
                placeholder={t(
                  'traceability.materialLotManager.form.fields.lotNumber.placeholder'
                )}
              />
            </div>

            <p className="mt-3 text-2xs text-muted-foreground">
              {t('traceability.materialLotManager.form.idempotencyHint')}
            </p>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Button className="flex-1" onClick={handleFindOrCreate} loading={isSaving}>
                {isSaving ? (
                  t('traceability.materialLotManager.form.submit.saving')
                ) : (
                  <>
                    <Plus aria-hidden />
                    {t('traceability.materialLotManager.form.submit.findOrCreate')}
                  </>
                )}
              </Button>
              <Button
                className="flex-1"
                variant="outline"
                onClick={handleReset}
                disabled={isSaving}
              >
                <RotateCcw aria-hidden />
                {t('traceability.materialLotManager.form.submit.reset')}
              </Button>
            </div>

            {successId !== null && successDetails !== null && (
              <Callout
                tone="success"
                className="mt-4"
                title={t('traceability.materialLotManager.form.result.title')}
              >
                <p className="font-semibold">
                  {successDetails.supplier ||
                    t('traceability.materialLotManager.form.result.noSupplier')}{' '}
                  {successDetails.lotNumber ? `— ${successDetails.lotNumber}` : '— —'}
                </p>
                <p className="mt-0.5 font-mono text-2xs text-muted-foreground">
                  {t('traceability.materialLotManager.form.result.idLabel', {
                    id: successId,
                  })}
                </p>
              </Callout>
            )}
          </Card>

          {/* Right: lot list */}
          <Card padding="sm" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-semibold text-foreground">
                {t('traceability.materialLotManager.list.title', {
                  name: selectedMaterial?.name ?? '',
                })}
              </p>
              <span className="text-xs text-muted-foreground">
                {t('traceability.materialLotManager.list.count', { count: lots.length })}
              </span>
            </div>

            <div className="max-h-72 space-y-2 overflow-auto pr-1">
              {isLoadingLots && (
                <div className="flex items-center justify-center py-8">
                  <Spinner
                    size="lg"
                    className="text-primary"
                    label={t('traceability.materialLotManager.list.loading')}
                  />
                </div>
              )}
              {!isLoadingLots && lots.length === 0 && (
                <EmptyState
                  size="compact"
                  title={t('traceability.materialLotManager.list.emptyTitle')}
                  description={t('traceability.materialLotManager.list.emptyHint')}
                />
              )}
              {!isLoadingLots &&
                lots.map((lot) => (
                  <Card key={lot.id} tier="inset" padding="none" className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        {lot.supplier ? (
                          <p className="text-sm font-semibold text-foreground truncate">
                            {lot.supplier}
                          </p>
                        ) : (
                          <p className="text-sm italic text-muted-foreground">
                            {t('traceability.materialLotManager.list.row.noSupplier')}
                          </p>
                        )}
                        <p className="font-mono text-2xs text-muted-foreground">
                          {lot.lotNumber ?? '—'}
                        </p>
                      </div>
                      <Badge size="sm" className="font-mono">
                        #{lot.id}
                      </Badge>
                    </div>
                  </Card>
                ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
